import { FilterOutlined } from '@ant-design/icons'
import { Badge, Checkbox, Popover } from 'antd'
import React, { useEffect, useState } from 'react'

import { useCartStore } from '@/stores/cart/cartStore'
import { useUserInfoStore } from '@/stores/user/userInfoStore'

import cn from './CartAuthorFilter.module.scss'

interface Author {
  id: string
  name: string
}

interface CartAuthorFilterProps {
  title: string
  authors: Author[]
}

function CartAuthorFilter({ title, authors }: CartAuthorFilterProps) {
  const [open, setOpen] = useState(false)

  const { selectedAuthorIds, setSelectedAuthorIds } = useCartStore((s) => s)
  const { selectedOrganisation } = useUserInfoStore((s) => s)
  const isOrgSelected = Object.keys(selectedOrganisation || {}).length > 0

  const isApplied = selectedAuthorIds.length > 0

  useEffect(() => {
    if (!open) return
    const close = () => setOpen(false)
    window.addEventListener('scroll', close, { passive: true })
    return () => window.removeEventListener('scroll', close)
  }, [open])

  const handleToggle = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedAuthorIds([...selectedAuthorIds, id])
    } else {
      setSelectedAuthorIds(selectedAuthorIds.filter((x) => x !== id))
    }
  }

  const content = (
    <div className={cn.popoverContent} onClick={(e) => e.stopPropagation()}>
      <div className={cn.list}>
        {authors.length === 0 ? (
          <div className={cn.emptyText}>Нет авторов</div>
        ) : (
          authors.map((author) => {
            const isChecked = selectedAuthorIds.includes(author.id)
            return (
              <div
                key={author.id}
                className={cn.listItem}
                onClick={() => handleToggle(author.id, !isChecked)}
              >
                <Checkbox
                  checked={isChecked}
                  onChange={(e) => e.stopPropagation()}
                />
                <span className={cn.authorName}>{author.name}</span>
              </div>
            )
          })
        )}
      </div>
      {isApplied && (
        <div className={cn.footer}>
          <button
            className={cn.resetBtn}
            onClick={() => setSelectedAuthorIds([])}
          >
            Сбросить
          </button>
        </div>
      )}
    </div>
  )

  const headerClass = [
    cn.headerCell,
    isApplied ? cn.applied : '',
    open ? cn.opened : '',
    isOrgSelected ? cn.organisationSelected : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <Popover
      content={content}
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottom"
      align={{ overflow: { adjustX: false, adjustY: false } }}
      destroyTooltipOnHide={false}
      rootClassName={cn.popoverRoot}
    >
      <div className={headerClass} onClick={(e) => e.stopPropagation()}>
        <span className={cn.titleText}>{title}</span>
        <Badge
          count={selectedAuthorIds.length}
          size="small"
          offset={[2, -2]}
          classNames={{
            indicator: `${cn.countBadge} ${isOrgSelected ? cn.countBadgeOrg : ''}`,
          }}
        >
          <FilterOutlined className={cn.filterIcon} />
        </Badge>
      </div>
    </Popover>
  )
}

export default React.memo(CartAuthorFilter)
